"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarRange } from "lucide-react";
import { ContactStep } from "@/lib/types";
import type { ProjectedDate } from "@/hooks/scheduling/useProjectedDates";
interface ContactTimelineCardProps {
  steps: ContactStep[];
  dates: ProjectedDate[] | null;
  active: number;
  onSelect: (index: number) => void;
}
const ContactTimelineCard = t0 => {
  const $ = _c(42);
  const {
    steps,
    dates,
    active,
    onSelect
  } = t0;
  const step = steps[active];
  const date = dates?.[active] ?? null;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden={true} className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-500 group-hover/card:opacity-100" style={{
      background: "radial-gradient(600px circle at 50% 0%, rgba(178,209,39,0.10), transparent 70%)"
    }} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400"><CalendarRange size={15} /> Your timeline</p>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="relative flex items-center justify-between gap-4">{t2}<span className="flex items-center gap-2 text-[11px] font-medium text-white/50"><span className="relative flex size-2"><motion.span className="absolute inline-flex size-full rounded-full bg-primary" animate={{
            scale: [1, 2.2],
            opacity: [0.7, 0]
          }} transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: "easeOut"
          }} /><span className="relative inline-flex size-2 rounded-full bg-primary" /></span>If you send today</span></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 18
    };
    t5 = {
      opacity: 1,
      y: 0
    };
    t6 = {
      opacity: 0,
      y: -14
    };
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== step.eta) {
    t8 = <p className="text-[11px] font-black uppercase tracking-[0.25em] text-primary">{step.eta}</p>;
    $[7] = step.eta;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = date ? date.day : "--";
  let t10;
  if ($[9] !== t9) {
    t10 = <span className="text-7xl font-black leading-[0.8] tracking-tighter text-white tabular-nums md:text-8xl">{t9}</span>;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = date ? date.weekday : "\xA0";
  let t12;
  if ($[11] !== t11) {
    t12 = <span className="block text-lg font-black uppercase tracking-tighter text-white md:text-xl">{t11}</span>;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const t13 = date ? date.month : "\xA0";
  let t14;
  if ($[13] !== t13) {
    t14 = <span className="block text-sm font-semibold uppercase tracking-[0.2em] text-white/40">{t13}</span>;
    $[13] = t13;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] !== t12 || $[16] !== t14) {
    t15 = <span className="pb-1">{t12}{t14}</span>;
    $[15] = t12;
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t10 || $[19] !== t15) {
    t16 = <div className="mt-3 flex items-end gap-4">{t10}{t15}</div>;
    $[18] = t10;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== step.title) {
    t17 = <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/50">{step.title}</p>;
    $[21] = step.title;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== active || $[24] !== t16 || $[25] !== t17 || $[26] !== t8) {
    t18 = <div className="relative mt-8 min-h-40"><AnimatePresence mode="wait"><motion.div key={active} initial={t4} animate={t5} exit={t6} transition={t7}>{t8}{t16}{t17}</motion.div></AnimatePresence></div>;
    $[23] = active;
    $[24] = t16;
    $[25] = t17;
    $[26] = t8;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== active || $[29] !== dates || $[30] !== onSelect || $[31] !== steps) {
    let t20;
    if ($[33] !== active || $[34] !== dates || $[35] !== onSelect) {
      t20 = (s, i) => <button key={s.number} type="button" onClick={() => onSelect(i)} aria-label={`Step ${s.number}: ${s.title}`} aria-current={i === active} className="group/chip cursor-pointer text-left"><span className="relative block h-0.5 w-full overflow-hidden rounded-full bg-white/10">{i === active && <motion.span layoutId="contact-step-marker" transition={{
            type: "spring",
            stiffness: 320,
            damping: 30
          }} className="absolute inset-0 rounded-full bg-primary" />}</span><span className={`mt-2 block truncate text-[11px] font-bold tabular-nums transition-colors duration-300 ${i === active ? "text-primary" : "text-white/30 group-hover/chip:text-white/60"}`}>{dates?.[i] ? `${dates[i].weekdayShort} ${dates[i].day}` : s.number}</span></button>;
      $[33] = active;
      $[34] = dates;
      $[35] = onSelect;
      $[36] = t20;
    } else {
      t20 = $[36];
    }
    t19 = steps.map(t20);
    $[28] = active;
    $[29] = dates;
    $[30] = onSelect;
    $[31] = steps;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[37] !== t19) {
    t20 = <div className="relative mt-8 border-t border-white/10 pt-5"><div className="grid grid-cols-4 gap-2">{t19}</div></div>;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t18 || $[40] !== t20) {
    t21 = <div className="group/card relative overflow-hidden rounded-3xl border-2 border-black bg-accent-foreground p-6 shadow-[8px_8px_0_0_#b2d127] transition-shadow duration-300 hover:shadow-[12px_12px_0_0_#b2d127] md:p-8">{t1}{t3}{t18}{t20}</div>;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  return t21;
};
export default ContactTimelineCard;